'use client';

import { useState } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { Header } from '@/components/header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { categories, dashboardStats } from '@/lib/data';

const categoryData = categories.map(cat => ({
  name: cat.name,
  value: cat.bookCount,
  color: cat.color
}));

const monthlyData = [
  { month: '8月', newUsers: 45, activeUsers: 320 },
  { month: '9月', newUsers: 52, activeUsers: 345 },
  { month: '10月', newUsers: 38, activeUsers: 330 },
  { month: '11月', newUsers: 65, activeUsers: 380 },
  { month: '12月', newUsers: 78, activeUsers: 420 },
  { month: '1月', newUsers: 42, activeUsers: 390 },
];

const topBooks = [
  { name: '百年孤独', borrowCount: 156, author: '加西亚·马尔克斯' },
  { name: '人类简史', borrowCount: 134, author: '尤瓦尔·赫拉利' },
  { name: '红楼梦', borrowCount: 128, author: '曹雪芹' },
  { name: '深入理解计算机系统', borrowCount: 112, author: 'Randal E. Bryant' },
  { name: '三体', borrowCount: 108, author: '刘慈欣' },
  { name: '算法导论', borrowCount: 96, author: 'Thomas H. Cormen' },
  { name: '活着', borrowCount: 89, author: '余华' },
  { name: '机器学习', borrowCount: 87, author: '周志华' },
];

export default function StatisticsPage() {
  return (
    <div className="min-h-screen bg-[#0A192F]">
      <Header 
        title="统计报表" 
        subtitle="系统运营数据统计与分析"
        showSearch={false}
        showActions={false}
      />

      <div className="p-8 space-y-6">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-[#112240] border border-[#1d3557]">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#1d3557] data-[state=active]:text-[#64FFDA]">
              概览
            </TabsTrigger>
            <TabsTrigger value="books" className="data-[state=active]:bg-[#1d3557] data-[state=active]:text-[#64FFDA]">
              图书统计
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#1d3557] data-[state=active]:text-[#64FFDA]">
              用户统计
            </TabsTrigger>
            <TabsTrigger value="borrows" className="data-[state=active]:bg-[#1d3557] data-[state=active]:text-[#64FFDA]">
              借阅统计
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-[#112240] border-[#1d3557]">
                <CardHeader>
                  <CardTitle className="text-[#E6F1FF]">借阅趋势</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={dashboardStats.monthlyTrend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1d3557" />
                        <XAxis dataKey="month" stroke="#8892B0" />
                        <YAxis stroke="#8892B0" />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#112240', 
                            border: '1px solid #1d3557',
                            borderRadius: '8px',
                            color: '#E6F1FF'
                          }} 
                        />
                        <Line type="monotone" dataKey="borrows" stroke="#64FFDA" strokeWidth={2} name="借阅量" />
                        <Line type="monotone" dataKey="returns" stroke="#3B82F6" strokeWidth={2} name="归还量" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#112240] border-[#1d3557]">
                <CardHeader>
                  <CardTitle className="text-[#E6F1FF]">图书分类分布</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={categoryData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {categoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#112240', 
                            border: '1px solid #1d3557',
                            borderRadius: '8px',
                            color: '#E6F1FF'
                          }} 
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-[#112240] border-[#1d3557]">
              <CardHeader>
                <CardTitle className="text-[#E6F1FF]">热门图书排行</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topBooks} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#1d3557" />
                      <XAxis type="number" stroke="#8892B0" />
                      <YAxis dataKey="name" type="category" width={150} stroke="#8892B0" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#112240', 
                          border: '1px solid #1d3557',
                          borderRadius: '8px',
                          color: '#E6F1FF'
                        }} 
                      />
                      <Bar dataKey="borrowCount" fill="#64FFDA" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Books Tab */}
          <TabsContent value="books" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-[#112240] border-[#1d3557]">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-[#8892B0]">图书总量</p>
                    <p className="text-4xl font-bold text-[#E6F1FF] mt-2">4,296</p>
                    <p className="text-sm text-[#10B981] mt-1">+156 本月新增</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[#112240] border-[#1d3557]">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-[#8892B0]">可借阅</p>
                    <p className="text-4xl font-bold text-[#10B981] mt-2">3,892</p>
                    <p className="text-sm text-[#8892B0] mt-1">占比 90.6%</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[#112240] border-[#1d3557]">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-[#8892B0]">已借出</p>
                    <p className="text-4xl font-bold text-[#F59E0B] mt-2">404</p>
                    <p className="text-sm text-[#8892B0] mt-1">占比 9.4%</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-[#112240] border-[#1d3557]">
              <CardHeader>
                <CardTitle className="text-[#E6F1FF]">分类统计详情</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {categories.map((cat) => (
                    <div key={cat.id} className="flex items-center gap-4">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: cat.color }}
                      />
                      <span className="w-24 text-[#E6F1FF]">{cat.name}</span>
                      <div className="flex-1 h-2 bg-[#0A192F] rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ 
                            backgroundColor: cat.color,
                            width: `${(cat.bookCount / 4296) * 100}%`
                          }}
                        />
                      </div>
                      <span className="w-16 text-right text-[#8892B0]">{cat.bookCount}本</span>
                      <span className="w-16 text-right text-[#8892B0]">
                        {((cat.bookCount / 4296) * 100).toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-[#112240] border-[#1d3557]">
                <CardHeader>
                  <CardTitle className="text-[#E6F1FF]">用户增长趋势</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1d3557" />
                        <XAxis dataKey="month" stroke="#8892B0" />
                        <YAxis stroke="#8892B0" />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#112240', 
                            border: '1px solid #1d3557',
                            borderRadius: '8px',
                            color: '#E6F1FF'
                          }} 
                        />
                        <Bar dataKey="newUsers" fill="#64FFDA" name="新增用户" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="activeUsers" fill="#3B82F6" name="活跃用户" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#112240] border-[#1d3557]">
                <CardHeader>
                  <CardTitle className="text-[#E6F1FF]">用户角色分布</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: '读者', value: 1200, color: '#3B82F6' },
                            { name: '图书管理员', value: 45, color: '#64FFDA' },
                            { name: '管理员', value: 13, color: '#8B5CF6' },
                          ]}
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          <Cell fill="#3B82F6" />
                          <Cell fill="#64FFDA" />
                          <Cell fill="#8B5CF6" />
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#112240', 
                            border: '1px solid #1d3557',
                            borderRadius: '8px',
                            color: '#E6F1FF'
                          }} 
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Borrows Tab */}
          <TabsContent value="borrows" className="space-y-6">
            <Card className="bg-[#112240] border-[#1d3557]">
              <CardHeader>
                <CardTitle className="text-[#E6F1FF]">借阅数据统计</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dashboardStats.monthlyTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1d3557" />
                      <XAxis dataKey="month" stroke="#8892B0" />
                      <YAxis stroke="#8892B0" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#112240', 
                          border: '1px solid #1d3557',
                          borderRadius: '8px',
                          color: '#E6F1FF'
                        }} 
                      />
                      <Bar dataKey="borrows" fill="#64FFDA" name="借阅量" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="returns" fill="#3B82F6" name="归还量" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
